import { parseQueryString, htmlDecode, secondsToIsoDuration } from '@/util';
import { decode } from '@/base64';
import { publicAxios, axios } from '@/axios';
import { config } from '@/config';

// Map our frontend hostnames/origins to their corresponding LMS origins.
// Kept as a fallback for known/custom LMS domains.
const LMS_ORIGIN_BY_HOSTNAME = {
  'https://teo.training': 'https://academy.agristo.com',
  'https://teotrainingtest.be': 'https://academytest.agristo.com',
};

const TERMINATED_VERB = 'http://adlnet.gov/expapi/verbs/terminated';
const ATTEMPTED_VERB = 'http://adlnet.gov/expapi/verbs/attempted';
const PROGRESSED_VERB = 'http://adlnet.gov/expapi/verbs/progressed';
const EXPERIENCED_VERB = 'http://adlnet.gov/expapi/verbs/experienced';
const PASSED_VERB = 'http://adlnet.gov/expapi/verbs/passed';
const FAILED_VERB = 'http://adlnet.gov/expapi/verbs/failed';

class TinCanService {
  queries;
  lastProgress;
  reportedVerbs = new Set(); // Track reported verbs
  currentCourse = null; // Keep last seen course for keepalive
  _keepAliveStarted = false;
  _keepAliveTimer = null;
  _lastKeepAliveAt = 0;
  _sessionKey = null;
  _terminateOnCloseStarted = false;
  _loggedMissingLmsOrigin = false;

  constructor(xapidetails) {
    if (TinCanService._instance) {
      // Keep singleton behavior, but allow a new xAPI launch/session to refresh safely.
      TinCanService._instance.initialize(xapidetails);
      return TinCanService._instance;
    }

    this.initialize(xapidetails);
    TinCanService._instance = this;
  }

  static getInstance(xapidetails) {
    if (!TinCanService._instance) {
      new TinCanService(xapidetails);
    } else {
      // Important for SPA/resume flows where the singleton already exists
      // but the xAPI query parameters may have changed.
      TinCanService._instance.initialize(xapidetails);
    }

    return TinCanService._instance;
  }

  available() {
    return !!this.queries?.tincan;
  }

  initialize(xapidetails) {
    const nextQueries =
      xapidetails || parseQueryString(document.location.search);
    const nextSessionKey = this._getSessionKey(nextQueries);

    if (this._sessionKey && this._sessionKey !== nextSessionKey) {
      this._resetSessionState();
    }

    this.queries = nextQueries;
    this._sessionKey = nextSessionKey;

    // Start keepalive now that queries exist.
    this.startKeepAlive();

    // Wire terminate-on-close logic once. The handler reads current instance state,
    // so it remains valid when initialize() updates this.queries later.
    this._setupTerminateOnClose();
  }

  _getSessionKey(queries = {}) {
    return [
      queries.endpoint,
      queries.activity_id,
      queries.registration,
      queries.actor,
      queries.enroll_id,
      queries.course_id,
      queries.component_id,
      queries.lup_tincandata_id,
      queries.slt,
      queries.auth,
    ]
      .map((value) => String(value || ''))
      .join('|');
  }

  _resetSessionState() {
    if (this._keepAliveTimer) {
      clearInterval(this._keepAliveTimer);
    }

    this.lastProgress = undefined;
    this.reportedVerbs = new Set();
    this.currentCourse = null;
    this._keepAliveStarted = false;
    this._keepAliveTimer = null;
    this._lastKeepAliveAt = 0;
    this._loggedMissingLmsOrigin = false;
  }

  reportStatus(follow, course) {
    // Keep last course for keepalive usage.
    this.currentCourse = course || this.currentCourse;

    if (this.available()) {
      if (follow.progress == 0) {
        this.reportInit(follow, course);
      } else if (follow.progress >= 0.99) {
        this.reportFinal(follow, course);
        this.reportFinalAgristo(follow, course);
      } else {
        this.progress(follow, course);
      }
    }
  }

  /**
   * Base xAPI statement generator (actor, object, context).
   * Kept 100% compatible with original callers.
   */
  generateTincanStatement(follow, course) {
    let actor;
    try {
      actor = JSON.parse(decodeURIComponent(this.queries.actor));
    } catch (e) {
      actor = {};
    }

    if (Array.isArray(actor)) actor = actor[0];

    if (actor && typeof actor === 'object') {
      actor.mbox = Array.isArray(actor.mbox) ? actor.mbox[0] : actor.mbox || '';
      actor.name = Array.isArray(actor.name) ? actor.name[0] : actor.name || '';
    }

    // Safe defaults for keepalive or invalid course data.
    const courseType = course?.courseType || 'course';
    const courseName = course?.courseName || 'Course';
    const courseDescription = course?.courseDescription || '';

    return {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      actor: {
        objectType: 'Agent',
        mbox: actor.mbox || '',
        name: actor.name || '',
      },
      object: {
        id: htmlDecode(decodeURIComponent(this.queries.activity_id)),
        objectType: 'Activity',
        definition: {
          type: `http://adlnet.gov/expapi/activities/${courseType}`,
          name: { 'en-US': courseName },
          description: { 'en-US': courseDescription },
        },
      },
      context: {
        registration: this.queries.registration,
      },
    };
  }

  /**
   * Extracted out the progress-specific result fields.
   */
  buildProgressResult(follow, course) {
    return {
      completion: follow.progress >= 0.99,
      duration: secondsToIsoDuration(follow.totalTimeSpent),
      extensions: {
        'https://w3id.org/xapi/cmi5/result/extensions/progress':
          follow.progress,
      },
    };
  }

  progress(follow, course) {
    if (follow.progress === this.lastProgress) return;
    this.lastProgress = follow.progress;

    if (this.available()) {
      let progressStatement = this.generateTincanStatement(follow, course);
      progressStatement.verb = {
        id: PROGRESSED_VERB,
        display: { 'en-US': 'progressed' },
      };
      progressStatement.result = this.buildProgressResult(follow, course);

      this.sendStatement(progressStatement);
    }
  }

  reportInit(follow, course) {
    if (this.reportedVerbs.has(ATTEMPTED_VERB)) return;

    const stmt = this.generateTincanStatement(follow, course);
    stmt.verb = {
      id: ATTEMPTED_VERB,
      display: { 'en-US': 'attempted' },
    };
    stmt.result = {
      duration: secondsToIsoDuration(follow.totalTimeSpent),
    };

    this.sendStatement(stmt);
    this.reportedVerbs.add(stmt.verb.id);
  }

  async reportFinal(follow, course) {
    try {
      if (follow.tasksRemaining === 0) {
        let finalStatement = this.generateTincanStatement(follow, course);

        const scaled = follow.score / (follow.maxScore || 1);
        const minScore = course?.minScore ?? 0.5;
        const passed = scaled >= minScore;

        finalStatement.verb = {
          id: passed ? PASSED_VERB : FAILED_VERB,
          display: { 'en-US': passed ? 'passed' : 'failed' },
        };

        finalStatement.result = {
          success: passed,
          duration: secondsToIsoDuration(follow.totalTimeSpent),
          score: {
            scaled,
            raw: Math.round(follow.score),
            min: 0,
            max: Math.round(follow.maxScore),
          },
          completion: true,
        };

        this.sendStatement(finalStatement);
        this.reportedVerbs.add(finalStatement.verb.id);
      }

      // Send terminated if not yet sent.
      if (!this.reportedVerbs.has(TERMINATED_VERB)) {
        const terminateStatement = this.generateTincanStatement(follow, course);
        terminateStatement.verb = {
          id: TERMINATED_VERB,
          display: { 'en-US': 'terminated' },
        };
        terminateStatement.result = { completion: true };

        this.sendStatement(terminateStatement);
        this.reportedVerbs.add(terminateStatement.verb.id);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async reportFinalAgristo(follow, course) {
    if (follow.tasksRemaining !== 0) return;

    const scaled = follow.score / (follow.maxScore || 1);
    const minScore = course?.minScore ?? 0.5;
    const passed = scaled >= minScore;

    const result = {
      enrollmentId:
        parseFloat(this.queries.enroll_id) ||
        parseFloat(this.queries.course_id),
      dateCompleted: follow.endDate,
      status: follow?.maxScore ? (passed ? 'passed' : 'failed') : 'completed',
      percentage: passed ? Math.round(scaled * 100) : null,
    };

    axios
      .post(`${config.backend}/xapi/markcomplete`, result)
      .catch(console.error);
  }

  /**
   * Send a statement (via proxypost) using axios.
   */
  async sendStatement(statement) {
    const statementEndpoint = this._buildStatementEndpoint();
    if (!statementEndpoint) return;

    publicAxios
      .post(`${config.backend}/proxypost`, {
        url: statementEndpoint,
        body: statement,
        headers: { Authorization: this.queries.auth },
      })
      .catch((e) => console.error('[TinCan] sendStatement failed', e));
  }

  /**
   * Build the full TinCan statement endpoint with correctly encoded query params.
   */
  _buildStatementEndpoint() {
    if (!this.queries?.endpoint) {
      console.error('[TinCan] Missing endpoint for statement');
      return null;
    }

    const params = new URLSearchParams();

    const addParam = (key, value) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    };

    addParam('enroll_id', this.queries.enroll_id);
    addParam('component_id', this.queries.component_id);
    addParam('lup_tincandata_id', this.queries.lup_tincandata_id);
    addParam('slt', this.queries.slt);

    return `${decodeURIComponent(
      this.queries.endpoint
    )}statements?${params.toString()}`;
  }

  _getCurrentFrontendOrigin() {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }

    const host = config.hostname;
    if (!host) return null;

    if (/^https?:\/\//i.test(host)) {
      return host.replace(/\/$/, '');
    }

    return `https://${host}`.replace(/\/$/, '');
  }

  _getOriginFromUrl(url) {
    try {
      if (!url) return null;
      return new URL(decodeURIComponent(url)).origin;
    } catch (e) {
      return null;
    }
  }

  _isUsefulLmsOrigin(origin) {
    if (!origin) return false;

    const ownOrigin = this._getCurrentFrontendOrigin();
    if (ownOrigin && origin === ownOrigin) return false;

    // The CloudFront package/wrapper is not the LMS session origin.
    if (/\.cloudfront\.net$/i.test(new URL(origin).hostname)) return false;

    return true;
  }

  /**
   * Resolve likely LMS origins.
   *
   * Order is intentional:
   * 1. document.referrer, when available, because it is often the embedding LMS.
   * 2. endpoint origin, useful for LearnUpon/app.learnupon.com launches.
   * 3. static mapping, kept for known custom LMS domains such as Agristo.
   */
  _getLmsOrigins() {
    const origins = [];
    const addOrigin = (origin) => {
      if (this._isUsefulLmsOrigin(origin) && !origins.includes(origin)) {
        origins.push(origin);
      }
    };

    if (typeof document !== 'undefined') {
      addOrigin(this._getOriginFromUrl(document.referrer));
    }

    addOrigin(this._getOriginFromUrl(this.queries?.endpoint));

    const frontendOrigin = this._getCurrentFrontendOrigin();
    addOrigin(LMS_ORIGIN_BY_HOSTNAME[frontendOrigin]);

    // Also keep compatibility with existing config.hostname values that may
    // already include the scheme.
    if (config.hostname) {
      addOrigin(
        LMS_ORIGIN_BY_HOSTNAME[String(config.hostname).replace(/\/$/, '')]
      );
    }

    if (!origins.length && !this._loggedMissingLmsOrigin) {
      this._loggedMissingLmsOrigin = true;
      console.warn('[TinCan] No LMS origin available for health check', {
        frontendOrigin,
        configHostname: config.hostname,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
        endpoint: this.queries?.endpoint,
      });
    }

    return origins;
  }

  /**
   * Build LMS health-check URLs.
   * Example:
   *   {origin}/data-insight/learners/progressions/tincans/health/check
   *     ?slt=...
   *     &tincandata_id=...
   *     &component_id=...
   *     &skip_session_renew=false
   */
  _buildLmsHealthUrls() {
    const { slt, lup_tincandata_id, component_id } = this.queries || {};
    if (!slt || !lup_tincandata_id || !component_id) {
      console.warn(
        '[TinCan] Missing slt/lup_tincandata_id/component_id for health check'
      );
      return [];
    }

    const params = new URLSearchParams({
      slt: String(slt),
      tincandata_id: String(lup_tincandata_id),
      component_id: String(component_id),
      skip_session_renew: 'false',
    });

    return this._getLmsOrigins().map(
      (origin) =>
        `${origin}/data-insight/learners/progressions/tincans/health/check?${params.toString()}`
    );
  }

  /**
   * Backwards-compatible single URL helper for existing callers/debugging.
   */
  _buildLmsHealthUrl() {
    return this._buildLmsHealthUrls()[0] || null;
  }

  /**
   * HEAD ping to keep LMS session alive.
   * Fire-and-forget via fetch(no-cors) so CORS/CORP don't matter for us.
   */
  _pingLmsHealth() {
    const urls = this._buildLmsHealthUrls();
    if (!urls.length) return;

    if (typeof fetch === 'undefined') {
      console.error(
        '[TinCan] fetch not available; cannot send LMS health ping'
      );
      return;
    }

    for (const url of urls) {
      // We don't care about the response, only that the request is sent
      // with cookies so the LMS sees activity.
      fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        credentials: 'include',
        keepalive: true,
      }).catch((err) => {
        // Errors here are largely irrelevant to the keepalive semantics,
        // but useful for diagnostics.
        console.warn('[TinCan] LMS health ping error:', err);
      });
    }
  }

  _sendKeepAliveStatement(reason = 'interval', options = {}) {
    if (!this.available()) return;
    if (!this.queries?.actor) return;

    const statementEndpoint = this._buildStatementEndpoint();
    if (!statementEndpoint) return;

    // Reuse last real course if available.
    const stmt = this.generateTincanStatement(null, this.currentCourse);

    stmt.verb = {
      id: EXPERIENCED_VERB,
      display: { 'en-US': 'experienced' },
    };

    delete stmt.result; // ensure clean keepalive

    const payload = {
      url: statementEndpoint,
      body: stmt,
      headers: { Authorization: this.queries.auth },
    };

    // Prefer sendBeacon when the page is going hidden/freezing/unloading.
    // This makes the request more likely to be queued before the browser
    // throttles or freezes the iframe. For normal active-page keepalives,
    // use axios so failures remain visible.
    if (options.preferBeacon && typeof navigator !== 'undefined') {
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });

      const queued =
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(`${config.backend}/proxypost`, blob);

      if (queued) return;

      console.warn('[TinCan] keepalive beacon was not queued', { reason });
    }

    return publicAxios
      .post(`${config.backend}/proxypost`, payload)
      .catch((e) => {
        console.warn('[TinCan] keepalive statement failed', {
          reason,
          error: e,
        });
      });
  }

  _sendKeepAlive(reason = 'interval', options = {}) {
    const now = Date.now();

    if (this._lastKeepAliveAt) {
      const gap = now - this._lastKeepAliveAt;
      if (gap > 4 * 60 * 1000) {
        console.warn('[TinCan] keepalive gap detected', {
          reason,
          seconds: Math.round(gap / 1000),
        });
      }
    }

    this._lastKeepAliveAt = now;

    this._sendKeepAliveStatement(reason, options);
    this._pingLmsHealth();
  }

  /**
   * Keepalive every 2 minutes:
   * - active page: xAPI "experienced" ping via normal POST, so failures are visible.
   * - hidden/freezing/unloading page: prefer sendBeacon, so the request is queued sooner.
   * - LMS session ping via direct HEAD keepalive to LMS (no-cors).
   * - extra pings when the page becomes active again after sleep/throttling.
   */
  startKeepAlive() {
    if (this._keepAliveStarted) return;
    this._keepAliveStarted = true;

    // Immediately send one keepalive.
    this._sendKeepAlive('start');

    // Repeat every 2 minutes. Browser may throttle this in background/sleep.
    this._keepAliveTimer = setInterval(
      () => this._sendKeepAlive('interval'),
      2 * 60 * 1000
    );

    if (typeof window !== 'undefined' && !this._keepAliveWakeListenersStarted) {
      this._keepAliveWakeListenersStarted = true;

      window.addEventListener('focus', () => this._sendKeepAlive('focus'));
      window.addEventListener('online', () => this._sendKeepAlive('online'));
      window.addEventListener('pageshow', () =>
        this._sendKeepAlive('pageshow')
      );

      // Last chance before normal navigation/unload. The separate terminate
      // handler still sends the xAPI terminated statement.
      window.addEventListener('pagehide', (ev) => {
        if (!ev.persisted) {
          this._sendKeepAlive('pagehide', { preferBeacon: true });
        }
      });

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this._sendKeepAlive('visibility-hidden', { preferBeacon: true });
          } else {
            this._sendKeepAlive('visibility-visible');
          }
        });

        // Chromium/Edge Page Lifecycle events. Not supported everywhere,
        // but harmless where unavailable.
        document.addEventListener('freeze', () => {
          this._sendKeepAlive('freeze', { preferBeacon: true });
        });

        document.addEventListener('resume', () => {
          this._sendKeepAlive('resume');
        });
      }
    }
  }

  _sendBeaconStatement(statement) {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.sendBeacon !== 'function'
    ) {
      return false;
    }

    const statementEndpoint = this._buildStatementEndpoint();
    if (!statementEndpoint) return false;

    const payload = JSON.stringify({
      url: statementEndpoint,
      body: statement,
      headers: { Authorization: this.queries.auth },
    });

    const blob = new Blob([payload], { type: 'application/json' });
    const queued = navigator.sendBeacon(`${config.backend}/proxypost`, blob);

    if (!queued) {
      console.warn('[TinCan] beacon statement was not queued');
    }

    return queued;
  }

  /**
   * Send a terminated statement via sendBeacon on real page unload
   * if it hasn't been sent yet.
   */
  _setupTerminateOnClose() {
    if (this._terminateOnCloseStarted) return;

    if (typeof window === 'undefined') {
      return;
    }

    this._terminateOnCloseStarted = true;

    const sendTerminate = () => {
      if (!this.available()) return;
      if (this.reportedVerbs.has(TERMINATED_VERB)) return;

      const stmt = this.generateTincanStatement(null, this.currentCourse);
      stmt.verb = {
        id: TERMINATED_VERB,
        display: { 'en-US': 'terminated' },
      };

      // User left mid-session -> no full completion info; mark as not completed.
      stmt.result = { completion: false };

      const queued = this._sendBeaconStatement(stmt);

      if (!queued) {
        // Last-resort fallback. This may not complete during unload,
        // but it is better than silently dropping the terminate attempt.
        this.sendStatement(stmt);
      }

      this.reportedVerbs.add(stmt.verb.id);

      // One last LMS health ping on close.
      this._pingLmsHealth();
    };

    // pagehide is fired on real unload/navigation; ignore bfcache (persisted).
    window.addEventListener('pagehide', (ev) => {
      if (!ev.persisted) {
        sendTerminate();
      }
    });

    // Fallback for older browsers.
    window.addEventListener('beforeunload', () => {
      sendTerminate();
    });
  }
}

export { TinCanService };
